import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Container, TextField } from "@mui/material";
import { validateEmail } from "utils/functions";
import { createFeedback } from "context/actions/feedbacks";
import { getPageList } from "context/actions/pages";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import CustomSnackBar from "components/CustomSnackBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareFacebook, faSquareInstagram, faSquareYoutube, faSquareTwitter } from "@fortawesome/free-brands-svg-icons";
import { Mail, Phone, MapPin, Send, ArrowUpRight } from "lucide-react";

const Footer = () => {
  const [form, setForm] = useState({ name: "", email: "", feedback: "" });
  const [errors, setErrors] = useState({});
  const [snack, setSnack] = useState({ isOpen: false, msg: "", type: "success" });
  const { t } = useTranslation();
  const [menus, setMenus] = useState([]);

  useEffect(() => {
    getPageList(false).then(d => { if (d.status === "success") setMenus(d.data); });
  }, []);

  const send = async () => {
    const e = {};
    if (!form.name) e.name = true;
    if (!validateEmail(form.email)) e.email = true;
    if (!form.feedback) e.feedback = true;
    setErrors(e);
    if (Object.keys(e).length) {
      setSnack({ isOpen: true, msg: e.email ? t("invalid_email") : t("fill_form_full"), type: "error" });
      return;
    }
    const res = await createFeedback(form);
    if (res.status === "success") {
      setSnack({ isOpen: true, msg: t("send_successfully"), type: "success" });
      setForm({ name: "", email: "", feedback: "" });
    }
  };

  const socials = [
    { icon: faSquareFacebook, url: "https://www.facebook.com/khanlor.mn" },
    { icon: faSquareYoutube,  url: "https://www.youtube.com" },
    { icon: faSquareTwitter,  url: "https://x.com" },
    { icon: faSquareInstagram,url: "https://www.instagram.com" },
  ];

  const RenderLinks = ({ menu }) => (
    <>
      {!menu.children?.length && (
        <Link href={menu.url || `/content/${menu.id}`} target={menu.url && menu.url[0] !== "/" ? "_blank" : "_self"}
          style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, textDecoration: "none" }}
        >
          <ArrowUpRight size={13} color="#00B4D8" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13.5, color: "rgba(255,255,255,0.65)", transition: "color 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.color = "#fff"}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.65)"}
          >
            {i18n.language === "en" ? menu.nameEng : menu.name}
          </span>
        </Link>
      )}
      {menu.children?.map((ch, i) => <RenderLinks key={i} menu={ch} />)}
    </>
  );

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.05)",
      borderRadius: "10px",
      fontSize: 13.5,
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
      "&.Mui-focused fieldset": { borderColor: "#00B4D8" },
    },
    "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.35)", opacity: 1 },
  };

  return (
    <footer>
      {/* Main footer */}
      <div style={{
        background: "linear-gradient(160deg, #020B1E 0%, #001845 40%, #002D6B 80%, #003580 100%)",
        padding: "72px 0 48px",
        position: "relative", overflow: "hidden",
      }}>
        {/* Decorations */}
        <div style={{ position: "absolute", top: -100, right: -100, width: 500, height: 500, background: "radial-gradient(circle, rgba(0,180,216,0.08) 0%, transparent 65%)", borderRadius: "50%", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -80, left: -80, width: 400, height: 400, background: "radial-gradient(circle, rgba(244,164,10,0.06) 0%, transparent 65%)", borderRadius: "50%", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", position: "relative", zIndex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1fr 1.2fr 1.5fr", gap: 48 }} className="footer-grid">

            {/* Brand */}
            <div>
              <Link href="/" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, textDecoration: "none" }}>
                <Image src="/12.jpeg" alt="Logo" width={52} height={52} style={{ borderRadius: "50%", border: "2px solid rgba(255,255,255,0.15)" }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>Монголын Чих Хамар Хоолойн</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#00B4D8" }}>Эмч Нарын Нийгэмлэг</div>
                </div>
              </Link>
              <p style={{ fontSize: 13.5, color: "rgba(255,255,255,0.55)", lineHeight: 1.8, marginBottom: 24, maxWidth: 280 }}>
                Чих хамар хоолойн эмч нарын мэдлэг, мэргэжлийг дэмжих, хөгжүүлэхэд зориулагдсан ашгийн бус байгууллага.
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                {socials.map((s, i) => (
                  <button key={i} onClick={() => window.open(s.url, "_blank", "noopener,noreferrer")} style={{
                    width: 36, height: 36, borderRadius: 9,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,180,216,0.2)"; e.currentTarget.style.borderColor = "rgba(0,180,216,0.4)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
                  >
                    <FontAwesomeIcon icon={s.icon} style={{ color: "rgba(255,255,255,0.75)", fontSize: 17 }} />
                  </button>
                ))}
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 20 }}>Холбоос</h4>
              {menus.slice(0, 7).map((m, i) => <RenderLinks key={i} menu={m} />)}
            </div>

            {/* Contact */}
            <div>
              <h4 style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 20 }}>Хаяг</h4>
              {[
                { Icon: MapPin, text: "Сүхбаатар дүүрэг, 1-р хороо, Нарны зам 59Г өргөтгөлд 3 давхар" },
                { Icon: Phone, text: "90076001" },
                { Icon: Mail, text: "mon_entsociety.mn" },
              ].map(({ Icon, text }, i) => (
                <div key={i} style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-start" }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(0,180,216,0.12)", border: "1px solid rgba(0,180,216,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                    <Icon size={13} color="#00B4D8" />
                  </div>
                  <span style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 1.65 }}>{text}</span>
                </div>
              ))}
            </div>

            {/* Feedback */}
            <div>
              <h4 style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 20 }}>
                {t("contact") || "Санал хүсэлт"}
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { key: "name", placeholder: t("name") || "Нэр", multi: false },
                  { key: "email", placeholder: "И-мэйл", multi: false },
                  { key: "feedback", placeholder: t("feedback") || "Санал", multi: true, rows: 3 },
                ].map(f => (
                  <TextField key={f.key} fullWidth size="small"
                    value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    error={!!errors[f.key]}
                    multiline={f.multi} rows={f.rows}
                    sx={inputStyle}
                  />
                ))}
                <button onClick={send} style={{
                  background: "linear-gradient(135deg, #0047AB, #00B4D8)",
                  color: "#fff", border: "none", borderRadius: 10,
                  padding: "11px 0", fontSize: 13.5, fontWeight: 700,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "opacity 0.2s, transform 0.2s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = "0.9"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "none"; }}
                >
                  <Send size={14} /> {t("send") || "Илгээх"}
                </button>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", marginTop: 56, paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <p style={{ fontSize: 12.5, color: "rgba(255,255,255,0.3)", margin: 0 }}>
              {t("right_reserved") || "© 2024 Монголын Чих Хамар Хоолойн Эмч Нарын Нийгэмлэг"}
            </p>
            <a href="https://mon-orl.mn" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, color: "rgba(0,180,216,0.6)", textDecoration: "none" }}>
              mon-orl.mn
            </a>
          </div>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
        }
        @media (max-width: 580px) {
          .footer-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <CustomSnackBar open={snack.isOpen} msg={snack.msg} handleClose={() => setSnack({ ...snack, isOpen: false })} type={snack.type} />
    </footer>
  );
};

export default Footer;
